<template>
    <BaseCard
        as="article"
        padding="md"
        radius="lgr"
        data-testid="weekly-action-card"
        :data-action-id="action.id"
        :class="[
            'journeys-action-card flex min-w-0 flex-col',
            isCollapsedReviewComplete
                ? 'journeys-action-card--completed journeys-action-card--review-complete'
                : (variant === 'current' && action.status === 'completed') || (variant === 'review' && reviewOutcome === 'completed')
                  ? 'journeys-action-card--completed'
                  : 'bg-white hover:border-[#DAD4CB]',
        ]">
        <template v-if="isCollapsedReviewComplete">
            <div class="flex min-w-0 items-center justify-between gap-4">
                <div class="flex min-w-0 flex-col gap-1">
                    <h3 class="journeys-action-card__title">{{ action.title }}</h3>
                    <p v-if="reviewCompletionLabel" class="journeys-action-card__completion-date">
                        {{ reviewCompletionLabel }}
                    </p>
                </div>
                <BaseDropdownMenu :sections="reviewUndoMenuSections" :side-offset="6" @select="handleReviewUndoMenuSelect">
                    <template #trigger>
                        <button
                            data-testid="weekly-action-review-status-menu"
                            type="button"
                            class="journeys-action-card__done"
                            :disabled="reviewDisabled"
                            :aria-busy="reviewDisabled"
                            :aria-label="`${action.title} is done. Open actions.`">
                            Done
                            <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                        </button>
                    </template>
                </BaseDropdownMenu>
            </div>
        </template>

        <template v-else>
            <div class="flex min-w-0 flex-1 flex-col gap-1">
                <a
                    v-if="resourceTaxonomy"
                    data-testid="weekly-action-resource-link"
                    :href="resourceTaxonomy.href"
                    :target="resourceTaxonomy.target"
                    :rel="resourceTaxonomy.rel"
                    class="journeys-action-card__resource-link"
                    @click="handlePrimaryActionLinkClick">
                    <i class="bi text-[11px]" :class="taxonomy.icon" aria-hidden="true"></i>
                    {{ taxonomy.label }}
                </a>
                <span v-else class="journeys-action-card__taxonomy" :class="taxonomy.className">
                    <i class="bi text-[10px]" :class="taxonomy.icon" aria-hidden="true"></i>
                    {{ taxonomy.label }}
                </span>
                <div class="flex min-w-0 items-start justify-between gap-4">
                    <h3 class="journeys-action-card__title min-w-0 flex-1">
                        {{ action.title }}
                    </h3>

                    <template v-if="variant === 'current' && action.status !== 'completed'">
                        <a
                            v-if="primaryAction?.kind === 'link' && isInAppStartAction"
                            data-testid="weekly-action-start-role-play"
                            :href="primaryAction.href"
                            :target="primaryAction.target"
                            :rel="primaryAction.rel"
                            class="journeys-action-card__start"
                            @click="handlePrimaryActionLinkClick">
                            Start
                        </a>
                        <button
                            v-else-if="primaryAction?.kind === 'start_conversation'"
                            data-testid="weekly-action-start-conversation"
                            type="button"
                            class="journeys-action-card__start"
                            :disabled="isConversationLaunching"
                            :aria-busy="isConversationLaunching"
                            @click="startConversation">
                            Start
                        </button>
                        <button
                            v-else
                            data-testid="weekly-action-discuss"
                            type="button"
                            class="journeys-action-card__discuss"
                            :disabled="isConversationLaunching"
                            :aria-busy="isConversationLaunching"
                            @click="startDiscussion">
                            Discuss
                        </button>
                    </template>

                    <button
                        v-if="variant === 'review'"
                        data-testid="weekly-action-review-complete"
                        type="button"
                        class="journeys-action-card__commit"
                        :disabled="reviewDisabled"
                        :aria-busy="reviewDisabled"
                        :aria-label="`Mark ${action.title} as complete`"
                        @click="emit('set-review-outcome', action.id, 'completed')">
                        I did this
                    </button>
                </div>

                <p v-if="action.details" class="journeys-action-card__description" :class="{ 'journeys-action-card__description--review': variant === 'review' }">
                    {{ action.details }}
                </p>
            </div>

            <template v-if="variant !== 'review'">
                <div class="journeys-action-card__rule"></div>

                <div class="journeys-action-card__footer">
                    <button
                        data-testid="weekly-action-details-button"
                        type="button"
                        class="journeys-action-card__details"
                        :aria-label="`View details for ${action.title}`"
                        @click="emit('view-details', action.id)">
                        View details
                    </button>

                    <div class="flex shrink-0 flex-wrap items-center gap-2">
                        <template v-if="variant === 'current'">
                            <BaseDropdownMenu v-if="action.status === 'completed'" :sections="undoMenuSections" :side-offset="6" @select="handleUndoMenuSelect">
                                <template #trigger>
                                    <button
                                        data-testid="weekly-action-status-menu"
                                        type="button"
                                        class="journeys-action-card__done"
                                        :disabled="isUpdating"
                                        :aria-busy="isUpdating"
                                        :aria-label="`${action.title} is done. Open actions.`">
                                        <i class="bi bi-check-circle-fill" aria-hidden="true"></i>
                                        Done
                                        <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                                    </button>
                                </template>
                            </BaseDropdownMenu>
                            <button
                                v-else
                                data-testid="weekly-action-status-button"
                                type="button"
                                class="journeys-action-card__complete-ghost"
                                :disabled="isUpdating"
                                :aria-busy="isUpdating"
                                :aria-label="completionAriaLabel"
                                @click="emitStatusUpdate">
                                {{ completionLabel }}
                            </button>
                        </template>

                        <button
                            v-else
                            data-testid="toggle-weekly-action-selection"
                            type="button"
                            class="journeys-action-card__selection"
                            :class="{ 'journeys-action-card__selection--selected': isSelected }"
                            :aria-pressed="isSelected"
                            :aria-label="isSelected ? `Uncommit ${action.title}` : `Commit ${action.title} to your weekly actions`"
                            :disabled="selectionDisabled"
                            @click="emit('toggle-selection', action.id)">
                            <i v-if="isSelected" class="bi bi-check-lg" aria-hidden="true"></i>
                            {{ isSelected ? "Committed" : "Commit" }}
                        </button>
                    </div>
                </div>
            </template>
        </template>
    </BaseCard>
</template>

<script setup lang="ts">
import type { DevelopmentActionReviewOutcome } from "~vue/api/generated/development-journeys";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import { computed } from "vue";

import { formatWeekdayInTimezone } from "./timezonePresentation";
import { getWeeklyActionTaxonomyPresentation } from "./weeklyActionPresentation";
import { getWeeklyActionPrimaryAction, handleWeeklyActionPrimaryLinkClick, logWeeklyActionPrimaryActionClicked } from "./weeklyActionPrimaryAction";
import type { WeeklyAction, WeeklyActionStatus } from "./weeklyActions";

const props = withDefaults(
    defineProps<{
        action: WeeklyAction;
        rolePlaySetupUrl: string;
        isCurrentWeek?: boolean;
        isUpdating?: boolean;
        isConversationLaunching?: boolean;
        timezone?: string;
        variant?: "current" | "review" | "selection";
        isSelected?: boolean;
        selectionDisabled?: boolean;
        reviewDisabled?: boolean;
        reviewOutcome?: DevelopmentActionReviewOutcome["status"];
    }>(),
    {
        isCurrentWeek: false,
        isUpdating: false,
        isConversationLaunching: false,
        variant: "current",
        isSelected: false,
        selectionDisabled: false,
        reviewDisabled: false,
        reviewOutcome: undefined,
    },
);

const emit = defineEmits<{
    "update-status": [actionId: string, status: WeeklyActionStatus];
    "view-details": [actionId: string];
    "toggle-selection": [actionId: string];
    "set-review-outcome": [actionId: string, outcome: DevelopmentActionReviewOutcome["status"]];
    "start-conversation": [initialUserMessage: string];
    "discuss-action": [actionId: string];
}>();

const primaryAction = computed(() => getWeeklyActionPrimaryAction(props.action, props.rolePlaySetupUrl));
const taxonomy = computed(() => getWeeklyActionTaxonomyPresentation(props.action.channel));
const resourceTaxonomy = computed(() => (taxonomy.value.kind === "resource" && primaryAction.value?.kind === "link" ? primaryAction.value : null));
const isInAppStartAction = computed(() => props.action.channel === "role_play_with_nadia");

const markUndoneSections = (disabled: boolean): DropdownMenuSection[] => [
    { id: "actions", items: [{ id: "mark-undone", type: "button", label: "Mark undone", icon: "bi bi-arrow-counterclockwise", disabled }] },
];
const undoMenuSections = computed(() => markUndoneSections(props.isUpdating));
const reviewUndoMenuSections = computed(() => markUndoneSections(props.reviewDisabled));

function handleUndoMenuSelect(item: DropdownMenuItem) {
    if (item.id === "mark-undone") {
        emitStatusUpdate();
    }
}

function handleReviewUndoMenuSelect(item: DropdownMenuItem) {
    if (item.id === "mark-undone") {
        emit("set-review-outcome", props.action.id, "not_completed");
    }
}

function emitStatusUpdate() {
    if (!props.isUpdating) {
        emit("update-status", props.action.id, nextStatus.value);
    }
}

function handlePrimaryActionLinkClick(event: MouseEvent) {
    const action = primaryAction.value;
    if (action?.kind === "link") {
        void handleWeeklyActionPrimaryLinkClick(action, event);
    }
}

function startConversation() {
    if (props.isConversationLaunching) {
        return;
    }
    const action = primaryAction.value;
    if (action?.kind !== "start_conversation") {
        return;
    }
    logWeeklyActionPrimaryActionClicked(action);
    emit("start-conversation", action.initialUserMessage);
}

function startDiscussion() {
    if (!props.isConversationLaunching) {
        emit("discuss-action", props.action.id);
    }
}

function assertNever(value: never): never {
    throw new Error(`Unhandled Development Journeys action status: ${value}`);
}

const completionLabel = computed(() => "Mark done");
const completionAriaLabel = computed(() => `Mark ${props.action.title} as done`);
const isReviewComplete = computed(() => props.reviewOutcome === "completed");
const isCollapsedReviewComplete = computed(() => props.variant === "review" && isReviewComplete.value);
const reviewCompletionLabel = computed(() => {
    if (!isCollapsedReviewComplete.value) {
        return null;
    }
    if (props.action.status !== "completed") {
        return null;
    }
    const completedAt = new Date(props.action.status_changed_at);
    if (Number.isNaN(completedAt.getTime())) {
        return "done";
    }
    return `done ${formatWeekdayInTimezone(completedAt, props.timezone)}`;
});
const nextStatus = computed<WeeklyActionStatus>(() => {
    switch (props.action.status) {
        case "completed":
            return props.isCurrentWeek ? "pending" : "not_completed";
        case "not_completed":
        case "pending":
            return "completed";
        default:
            return assertNever(props.action.status);
    }
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.journeys-action-card {
    gap: 8px;
    padding: 20px;
    border-color: #e7e2db;
    border-radius: 10px;
    transition: border-color 150ms ease;
}

.journeys-action-card--completed {
    background: #fff;
}

.journeys-action-card--review-complete {
    gap: 0;
    padding: 16px 20px;
}

.journeys-action-card__title {
    color: #2a2723;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
}

.journeys-action-card__description {
    display: -webkit-box;
    overflow: hidden;
    color: #6e6860;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 13px;
    line-height: 18px;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
}

.journeys-action-card__description--review {
    min-height: 36px;
    -webkit-line-clamp: 2;
}

.journeys-action-card__completion-date {
    color: #6e6860;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 13px;
    line-height: 18px;
}

.journeys-action-card__taxonomy,
.journeys-action-card__resource-link {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 4px;
    height: 20px;
    padding: 1px 10px;
    border-radius: 9999px;
    color: #6e6860;
    font-family: var(--font-scaffold, "Space Grotesk", sans-serif);
    font-size: 11px;
    line-height: 18px;
}

.journeys-action-card__taxonomy--work {
    background: #f7f5f2;
    color: #2a2723;
}

.journeys-action-card__taxonomy--nadia {
    background: #c6e9e9;
    color: #1e6461;
}

.journeys-action-card__resource-link {
    height: auto;
    padding: 0;
    border-radius: 0;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
}

.journeys-action-card__rule {
    height: 1px;
    margin: 4px 0;
    background: #e7e2db;
}

.journeys-action-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.journeys-action-card__details,
.journeys-action-card__complete-ghost {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: #6e6860;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    transition: color 150ms ease;
}

.journeys-action-card__details:hover,
.journeys-action-card__complete-ghost:hover {
    color: #2a2723;
}

.journeys-action-card__commit,
.journeys-action-card__discuss,
.journeys-action-card__done,
.journeys-action-card__selection {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 5px 14px;
    border: 1px solid #dad4cb;
    border-radius: 8px;
    background: #fff;
    color: #2a2723;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    transition:
        background 150ms ease,
        border-color 150ms ease,
        color 150ms ease;
}

.journeys-action-card__commit {
    order: 1;
}

.journeys-action-card__commit:hover {
    background: #f7f5f2;
}

.journeys-action-card__discuss:hover {
    background: #f7f5f2;
}

.journeys-action-card__start {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 88px;
    padding: 5px 14px;
    border-radius: 8px;
    background: #f1eee9;
    color: #2a2723;
    font-family: var(--font-app, Inter, sans-serif);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    transition: background 150ms ease;
}

.journeys-action-card__start:hover {
    background: #e7e2db;
}

.journeys-action-card__selection:hover {
    background: #f7f5f2;
}

.journeys-action-card__selection--selected,
.journeys-action-card__selection--selected:hover {
    border-color: transparent;
    background: #c6e9e9;
    color: #1e6461;
}

.journeys-action-card__done {
    border-color: transparent;
    border-radius: 9999px;
    background: #deefd0;
    color: #3b654d;
}

.journeys-action-card__done:hover {
    background: #d3e8c1;
}

.journeys-action-card__commit:disabled,
.journeys-action-card__discuss:disabled,
.journeys-action-card__start:disabled,
.journeys-action-card__done:disabled,
.journeys-action-card__complete-ghost:disabled,
.journeys-action-card__selection:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}
</style>
